import { Controller } from "@hotwired/stimulus"

// Live counters, checks and previews for the post composer. The limits come
// from shared/social_platforms/<provider>.json, embedded in the page, which
// the server checks the post against too, so what this flags is what the
// server would refuse. Media files' own limits (size, length, shape) are
// checked by the server when the post is submitted.
export default class extends Controller {
  static targets = ["base", "account", "media", "panel", "emptyNote"]
  static values = { rules: Object }

  connect() {
    this.refresh()
  }

  refresh() {
    const chosen = new Set(this.accountTargets.filter((box) => box.checked).map((box) => box.dataset.accountId))
    const media = this.mediaTargets.filter((input) => input.value)
    this.emptyNoteTarget.classList.toggle("hidden", chosen.size > 0)

    this.panelTargets.forEach((panel) => {
      const visible = chosen.has(panel.dataset.accountId)
      panel.classList.toggle("hidden", !visible)
      panel.classList.toggle("flex", visible)
      if (visible) this.check(panel, this.panelMedia(panel, media))
    })
  }

  // An account set to use media of its own is checked against its own list,
  // which shows only while the switch is on.
  panelMedia(panel, media) {
    const toggle = panel.querySelector("[data-role=own-media-toggle]")
    const own = panel.querySelector("[data-role=own-media]")
    if (!toggle || !own) return media
    own.hidden = !toggle.checked
    if (!toggle.checked) return media
    return [...own.querySelectorAll("[data-social-media-picker-target=list] input[data-role=value]")].filter((input) => input.value)
  }

  check(panel, media) {
    const rules = this.rulesValue[panel.dataset.rulesKey]
    if (!rules) return

    const override = panel.querySelector("[data-role=body]").value.trim()
    const text = override || this.baseTarget.value
    const format = this.format(panel, rules.provider, media)
    const problems = []
    let counter = ""

    if (rules.provider === "youtube") {
      const title = panel.querySelector("[data-role=yt-title]")?.value || ""
      const description = panel.querySelector("[data-role=yt-description]")?.value || text
      counter = `Description ${this.length(description)} / ${rules.text.description_max_chars}`
      if (this.length(title) > rules.text.title_max_chars) problems.push(`The title is over ${rules.text.title_max_chars} characters.`)
      if (this.length(description) > rules.text.description_max_chars) problems.push(`The description is over ${rules.text.description_max_chars} characters.`)
    } else if (rules.provider === "x") {
      const thread = (panel.querySelector("[data-role=thread]")?.value || "").split(/^\s*---\s*$/m).map((part) => part.trim()).filter(Boolean)
      const parts = [text, ...thread].filter((part) => part.trim() !== "")
      counter = parts.map((part) => `${this.length(part)} / ${rules.text.max_chars}`).join(" · ")
      parts.forEach((part, index) => {
        if (this.length(part) > rules.text.max_chars) problems.push(`${parts.length > 1 ? `Post ${index + 1} of the thread` : "The text"} is over ${rules.text.max_chars} characters.`)
      })
      if (parts.length > rules.text.thread_max_posts) problems.push(`A thread takes up to ${rules.text.thread_max_posts} posts.`)
      const choices = (panel.querySelector("[data-role=poll-options]")?.value || "").split("\n").map((choice) => choice.trim()).filter(Boolean)
      if (choices.length > 0 && rules.poll) {
        const poll = rules.poll
        if (choices.length < poll.min_options || choices.length > poll.max_options) problems.push(`A poll needs ${poll.min_options} to ${poll.max_options} choices.`)
        choices.forEach((choice, index) => {
          if (this.length(choice) > poll.option_max_chars) problems.push(`Poll choice ${index + 1} is over ${poll.option_max_chars} characters.`)
        })
        if (media.length > 0) problems.push("An X post with a poll cannot carry media.")
      }
    } else if (rules.text.max_chars) {
      counter = `${this.length(text)} / ${rules.text.max_chars}`
      if (this.length(text) > rules.text.max_chars) problems.push(`The text is over ${rules.text.max_chars} characters.`)
      const hashtags = (text.match(/(?:^|\s)#\w/g) || []).length
      if (rules.text.max_hashtags && hashtags > rules.text.max_hashtags) problems.push(`${rules.label} takes up to ${rules.text.max_hashtags} hashtags.`)
    }

    const spec = rules.formats[format]
    if (spec) {
      const counts = spec.media || {}
      if ((counts.min || 0) > media.length) problems.push(`${rules.label} ${spec.label.toLowerCase()} needs ${counts.min === 1 ? "an image or video" : `at least ${counts.min} files`}.`)
      if (counts.max !== undefined && media.length > counts.max) problems.push(`${rules.label} ${spec.label.toLowerCase()} takes ${counts.max === 1 ? "one file" : `up to ${counts.max} files`}.`)
      media.forEach((box) => {
        if (counts.kinds && !counts.kinds.includes(box.dataset.kind)) problems.push(`${box.dataset.title}: ${rules.label} ${spec.label.toLowerCase()} doesn't take ${box.dataset.kind}s.`)
      })
    }
    if (!text.trim() && media.length === 0) problems.push("Add some text or media.")

    panel.querySelector("[data-role=counter]").textContent = counter
    const list = panel.querySelector("[data-role=problems]")
    list.replaceChildren(...problems.map((problem) => Object.assign(document.createElement("li"), { textContent: problem })))
    this.preview(panel, text, media, spec)
  }

  // Mirrors Social::PostVariant#format.
  format(panel, provider, media) {
    const kinds = media.map((box) => box.dataset.kind)
    const chosen = panel.querySelector("select[name$='[format]']")?.value
    switch (provider) {
      case "facebook_page": return chosen || "post"
      case "instagram": return chosen || "feed"
      case "linkedin": return kinds.includes("document") ? "document" : kinds.includes("video") ? "video" : "post"
      case "tiktok": return kinds.length > 0 && kinds.every((kind) => kind === "image") ? "photo" : "video"
      case "youtube": return panel.querySelector("[data-role=short]")?.checked ? "short" : "video"
      default: return "post"
    }
  }

  preview(panel, text, media, spec) {
    panel.querySelector("[data-role=preview-text]").textContent = text
    const image = panel.querySelector("[data-role=preview-media]")
    const first = media[0]
    image.classList.toggle("hidden", !first)
    if (first) image.src = first.dataset.preview
    panel.querySelector("[data-role=preview-format]").textContent = spec ? `${spec.label}${media.length > 1 ? ` · ${media.length} files` : ""}` : ""
  }

  // Counts characters the way the server does (code points), not UTF-16 units.
  length(text) {
    return [...text].length
  }
}
